import styled from 'styled-components';
import { SkeletonSelectionButton } from './sportsbook-selection-button';
import { FC, type JSX } from 'react';
import { SelectionDto, SelectionsTemplate } from '../api/sbapi/sbapi-api-model';
import { SELECTION_OUTCOME_TYPE_DEFAULT } from '../sportsbook-constants';

const FIRST_ROW = 1;

const Grid = styled.div`
	display: grid;
	gap: 2px;
`;

const GridFloating = styled(Grid)<{ columnCount?: number }>`
	grid-auto-rows: 1fr;
	grid-template-columns: repeat(${({ columnCount }) => columnCount || '3'}, 1fr);
`;

const GridCell = styled.div<{ row?: number; column?: number }>`
	display: flex;
	min-height: 37px;
	min-width: 0;
	${({ row }) => row && `grid-row: ${row};`}
	${({ column }) => column && `grid-column: ${column};`}
`;

type Props = {
	className?: string;
	columnCount?: number;
	selections: SelectionDto[];
	selectionsTemplate?: SelectionsTemplate;
	outcomeTypeMap?: { [key: string]: number };
	renderSelection: (selection: SelectionDto) => JSX.Element;
};

const SportsbookMarketSelectionsFloating: FC<Props> = ({
	className,
	selections,
	selectionsTemplate,
	columnCount,
	outcomeTypeMap,
	renderSelection
}) => {
	if (selectionsTemplate) {
		return (
			<GridFloating columnCount={columnCount} className={className}>
				{selectionsTemplate.map(({ id, order }, index) => {
					const selectionWithTemplateId = selections.find(selection => String(selection.templateId) === id);
					const cell = index + 1;

					if (!selectionWithTemplateId) {
						return <GridCell key={`empty-cell-${cell}`} />;
					}

					return (
						<GridCell key={selectionWithTemplateId.id} column={order + 1} row={FIRST_ROW}>
							{renderSelection(selectionWithTemplateId)}
						</GridCell>
					);
				})}
			</GridFloating>
		);
	}

	if (!selectionsTemplate && outcomeTypeMap) {
		return (
			<GridFloating columnCount={columnCount} className={className}>
				{selections.map(selection => {
					const cellPosition = outcomeTypeMap[selection.outcomeType ?? SELECTION_OUTCOME_TYPE_DEFAULT];

					return (
						<GridCell key={selection.id} column={cellPosition} row={FIRST_ROW}>
							{renderSelection(selection)}
						</GridCell>
					);
				})}
			</GridFloating>
		);
	}

	return (
		<GridFloating columnCount={columnCount} className={className}>
			{selections.map(selection => {
				return (
					<GridCell key={selection.id} row={FIRST_ROW}>
						{renderSelection(selection)}
					</GridCell>
				);
			})}
		</GridFloating>
	);
};

export default SportsbookMarketSelectionsFloating;

export const SkeletonMarketSelections = ({ className = '', columnCount = 3 }) => {
	const selections = new Array(columnCount).fill(null);

	return (
		<GridFloating columnCount={columnCount} className={className}>
			{selections.map((_, index) => {
				const key = `skeleton-market-selection-${index}`;
				return <SkeletonSelectionButton key={key} />;
			})}
		</GridFloating>
	);
};
